import { useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { ArrowLeft } from '@phosphor-icons/react'

import TextField from '../FormLegacy/FormFieldsV2/TextField'
import { formatMoney } from '../../helpers/helpers'
import { getAssetLink } from '../../helpers/get-asset-link'
import API from '../../api/api'

import HolidayTipsPaymentResult from './HolidayTipsPaymentResult'
import { TIP_OPTIONS, TIP_MULTIPLIER } from './helpers'

const HolidayTipsPaymentElement = ({
  member,
  tip,
  totalAmount,
  setTip = () => {},
  setMember = () => {},
}) => {
  const { t } = useTranslation(['holiday-tips'])
  const [paymentStatus, setPaymentStatus] = useState(null)
  const [isComplete, setIsComplete] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)

  const stripe = useStripe()
  const elements = useElements()

  const handlePay = async (event) => {
    event.preventDefault()
    if (!stripe || !elements) {
      return
    }

    setIsProcessing(true)

    const { error: submitError } = await elements.submit()
    if (submitError) {
      setIsProcessing(false)
      return
    }

    try {
      const response = await API.tipsPayment(totalAmount, member.name)
      const clientSecret = response.data.clientSecret

      const result = await stripe.confirmPayment({
        elements,
        clientSecret,
        confirmParams: {
          return_url: `${window.location.origin}/optimatips?status=success`,
        },
        redirect: 'if_required',
      })

      if (result.error) {
        console.error('Payment failed:', result.error)
        setPaymentStatus('error')
      } else {
        setPaymentStatus('success')
      }
    } catch {
      setPaymentStatus('error')
    } finally {
      setIsProcessing(false)
    }
  }

  const handleClose = () => {
    if (paymentStatus === 'success') {
      setMember(null)
    } else {
      setPaymentStatus(null)
    }
  }

  const handleTipChange = (e) => setTip(e.target.value)

  if (paymentStatus) {
    return <HolidayTipsPaymentResult type={paymentStatus} onClose={handleClose} />
  }

  return (
    <div className="holiday-tips__payment">
      <div>
        <button className="button" onClick={() => setMember(null)}>
          <ArrowLeft size={24} />
        </button>
        <span className="holiday-tips__payment-title">
          {t('holiday-tips:payment.sendTipToLabel')}
        </span>
        <div className="holiday-tips__payment-member">
          <img
            src={getAssetLink({ path: 'chitown-avatar/avatar', file: member.image })}
            alt={t('holiday-tips:shared.memberImageAlt')}
          />
          <div>
            <span className="holiday-tips__payment-member-name">{member.name}</span>
            <span className="holiday-tips__payment-member-position">{member.position}</span>
          </div>
        </div>
      </div>
      <div className="holiday-tips__selector">
        <div className="holiday-tips__selector-title">
          {t('holiday-tips:payment.selectorTitle')}
        </div>
        <div className="holiday-tips__selector-options">
          {TIP_OPTIONS.map((option) => (
            <button
              key={option}
              className={`button ${tip === option ? 'button-black' : 'button-grey'}`}
              onClick={() => setTip(option)}
            >
              {t('holiday-tips:payment.tipOptionAmount', { amount: option })}
            </button>
          ))}
        </div>
        <div className="holiday-tips__selector-custom">
          <div className="holiday-tips__selector-input-group">
            <span>{t('holiday-tips:payment.customValuePrefix')}</span>
            <div>
              <span>{t('holiday-tips:payment.tipLabel')}</span>
              <TextField
                type="number"
                placeholder={t('holiday-tips:payment.tipPlaceholder')}
                name="tip"
                value={tip}
                min={0}
                onChange={handleTipChange}
              />
            </div>
          </div>
        </div>
        <div className="holiday-tips__divider" />
        <div className="holiday-tips__total">
          <span>{t('holiday-tips:payment.totalLabel')}</span>
          <span>{formatMoney(totalAmount / TIP_MULTIPLIER)}</span>
        </div>
      </div>
      <form onSubmit={handlePay} className="holiday-tips__payment-form">
        <PaymentElement onChange={(e) => setIsComplete(e.complete)} />
        <button
          disabled={!stripe || isProcessing || !tip || !isComplete}
          className={`button is-block is-black ${isProcessing ? 'is-loading' : ''}`}
        >
          {t('holiday-tips:payment.payButton', {
            amount: formatMoney(totalAmount / TIP_MULTIPLIER),
          })}
        </button>
      </form>
    </div>
  )
}

HolidayTipsPaymentElement.propTypes = {
  member: PropTypes.object.isRequired,
  tip: PropTypes.string.isRequired,
  totalAmount: PropTypes.number.isRequired,
  setTip: PropTypes.func.isRequired,
  setMember: PropTypes.func.isRequired,
}

export default HolidayTipsPaymentElement
